import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./ComplexReactionLogin.css";

const ComplexReactionLogin = () => {
  const navigate = useNavigate();

  const [service, setService] = useState("");
  const [loading, setLoading] = useState(true);

  // ==========================================
  // SCROLL TO TOP
  // ==========================================

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // ==========================================
  // ACCESS CHECK
  // ==========================================

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      const email = localStorage.getItem("user_email");
      const authed = localStorage.getItem("rg_auth") === "1";

      if (!authed || !email) {
        navigate("/dashboard-login");
        return;
      }

      try {
        const res = await axios.get("/check-access", {
          params: {
            email,
            test: "complex_reaction",
          },
        });

        console.log("Complex Reaction API:", res.data);

        if (!res.data.allowed) {
          alert("Access denied");
          navigate("/");
          return;
        }

        // Get service from API response or localStorage
        const userService =
          res.data.service ||
          res.data.metadata?.service ||
          res.data.user?.metadata?.service ||
          localStorage.getItem("service") ||
          "";

        if (isMounted) {
          setService(userService);
        }
      } catch (err) {
        console.error(
          "Complex reaction access check failed:",
          err
        );

        alert("Unable to verify access.");
        navigate("/");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // ==========================================
  // SERVICE IDENTIFICATION
  // ==========================================

  const normalizedService = service.trim().toLowerCase();

  const isVisionIgnite =
    normalizedService === "forge your peak - vision ignite";

  const isIgniteOrAscend =
    normalizedService === "forge your peak - ignite" ||
    normalizedService === "forge your peak - ascend";

  // ==========================================
  // RETURN HOME
  // ==========================================

  const returnHome = () => {
    if (isVisionIgnite) {
      navigate("/measure");
    } else if (isIgniteOrAscend) {
      navigate("/ignite/dashboard");
    } else {
      navigate("/");
    }
  };

  // ==========================================
  // START TEST
  // ==========================================

  const startTest = () => {
    navigate("/complex-reaction/play");
  };

  // ==========================================
  // LOADING SCREEN
  // ==========================================

  if (loading) {
    return (
      <div className="rg-login-wrapper">
        <p style={{ textAlign: "center" }}>
          Checking access...
        </p>
      </div>
    );
  }

  // ==========================================
  // SERVICE-BASED CONTENT
  // Ignite + Ascend = Existing content
  // Vision Ignite = Separate content
  // ==========================================

  const pageTitle = isVisionIgnite
    ? "Complex Reaction Assessment"
    : "Reaction Speed (Complex)";

  const pageDescription = isVisionIgnite
    ? "Complex reaction speed measures how quickly you identify visual signals, make the correct decision, and respond. It combines visual processing, attention, and decision-making."
    : "Complex reaction speed measures how fast you can recognize, decide, and respond correctly when multiple signals are possible. It's not just reacting — it's See → Decide → Execute.";

  return (
    <div className="rg-login-wrapper">

      {/* ==========================================
          RETURN HOME BUTTON
      ========================================== */}

      <div className="return-home-wrapper">
        <button
          type="button"
          onClick={returnHome}
          className="return-home-btn"
        >
          ← Return Home
        </button>
      </div>

      {/* ==========================================
          HEADER SECTION
      ========================================== */}

      <div className="reaction-header-card">

        <h2 className="reaction-title">
          {pageTitle}
        </h2>

        <p className="reaction-subtitle">
          {pageDescription}
        </p>

        <div className="reaction-pill">
          Test type:{" "}
          <strong>
            Multiple visual stimuli → Correct decision response
          </strong>
        </div>

        <hr className="reaction-divider" />

        {/* ==========================================
            WHY SECTION
        ========================================== */}

        <div className="reaction-why-card">

          <h3 className="why-title">
            {isVisionIgnite
              ? "Why Complex Reaction Speed Matters"
              : "Why complex reaction speed matters in sport"}
          </h3>

          {isVisionIgnite ? (
            <>
              {/* VISION IGNITE CONTENT */}

              <p className="why-description">
                Complex reaction speed involves detecting a
                visual signal, identifying the correct response,
                and acting accurately. This assessment helps
                you understand how you respond when different
                visual signals require different decisions.
              </p>

              <div className="why-tags">
                <span>Visual Processing</span>
                <span>Decision Making</span>
                <span>Attention</span>
                <span>Response Accuracy</span>
                <span>Focus</span>
              </div>

              <ul className="why-list">
                <li>
                  <strong>Visual recognition:</strong>{" "}
                  Identify the relevant visual signal.
                </li>

                <li>
                  <strong>Decision making:</strong>{" "}
                  Choose the correct response based on the signal.
                </li>

                <li>
                  <strong>Selective attention:</strong>{" "}
                  Respond to the target while ignoring signals
                  that do not require a response.
                </li>

                <li>
                  <strong>Response accuracy:</strong>{" "}
                  Evaluate how quickly and correctly you respond
                  across repeated trials.
                </li>
              </ul>
            </>
          ) : (
            <>
              {/* ORIGINAL IGNITE + ASCEND CONTENT */}

              <p className="why-description">
                Real game situations require decision-making
                under pressure. You must read cues, filter
                distractions, choose the correct option,
                and act instantly. Complex reaction speed
                measures your cognitive processing under
                competitive speed.
              </p>

              <div className="why-tags">
                <span>Faster decision making</span>
                <span>Better anticipation</span>
                <span>Smarter positioning</span>
                <span>Reduced hesitation</span>
                <span>Improved game IQ</span>
              </div>
            </>
          )}

        </div>
      </div>

      {/* ==========================================
          HOW GAME WORKS
      ========================================== */}

      <div className="how-card">

        <h2 className="how-title">
          How the game works
        </h2>

        <ul className="how-list">

          <li>
            A circle will appear in different colors.
          </li>

          <li>
            Click ONLY when the circle turns{" "}
            <strong>GREEN.</strong>
          </li>

          <li>
            Ignore <strong>RED</strong> and{" "}
            <strong>BLUE.</strong>
          </li>

          <li>
            You will get <strong>5 attempts</strong> and your
            reaction time will be recorded.
          </li>

          <li>
            Faster correct reactions improve your score.
          </li>

        </ul>

        <p className="how-note">
          Best practice: use the same device and posture
          when comparing results.
        </p>

      </div>

      {/* ==========================================
          START TEST
      ========================================== */}

      <div className="start-test-section">

        <h2>
          {isVisionIgnite
            ? "Ready to Assess Your Complex Reaction Speed?"
            : "Ready to test your reaction?"}
        </h2>

        <button
          type="button"
          className="start-test-btn"
          onClick={startTest}
        >
          Start Test
        </button>

      </div>

    </div>
  );
};

export default ComplexReactionLogin;